export type ContactStatusState = "idle" | "loading" | "success" | "error";

export interface ContactFormOptions {
  /** Formspree / Web3Forms / Netlify Forms 等のPOST先URL。未設定なら mailto フォールバック */
  endpoint?: string;
  /** mailto フォールバック先メールアドレス */
  mailto?: string;
  /** fetch 実装（テストで差し替え可能） */
  fetchImpl?: typeof fetch;
}

const FIELD_LABELS: Record<string, string> = {
  useCase: "ご用途",
  name: "お名前",
  email: "メールアドレス",
  phone: "お電話番号",
  area: "設置予定エリア",
  facilityName: "施設名",
  facilityType: "業態",
  units: "導入想定台数",
  placement: "設置予定場所",
  message: "ご相談内容",
};

const USE_CASE_LABELS: Record<string, string> = {
  business: "宿泊施設・法人",
  private: "個人のお客様",
};

const FACILITY_LABELS: Record<string, string> = {
  glamping: "グランピング",
  ryokan: "旅館・ホテル",
  villa: "一棟貸し・ヴィラ",
  other: "その他",
};

const PLACEMENT_LABELS: Record<string, string> = {
  garden: "自宅の庭",
  rooftop: "屋上",
  villa: "別荘",
  other: "その他",
};

export function initContactForm(
  form: HTMLFormElement | null,
  opts: ContactFormOptions = {},
): void {
  if (!form) return;

  const status = form.querySelector<HTMLElement>("[data-contact-status]");
  const submitBtn = form.querySelector<HTMLButtonElement>("button[type='submit']");
  const fetcher = opts.fetchImpl ?? globalThis.fetch?.bind(globalThis);

  const setStatus = (msg: string, state: ContactStatusState) => {
    if (!status) return;
    status.textContent = msg;
    status.dataset.state = state;
  };

  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    // Honeypot anti-spam: 通常ユーザーは見えないので空のはず
    // Web3Forms は `botcheck` field を honeypot として認識する
    const honeypot = form.querySelector<HTMLInputElement>("input[name='botcheck']");
    if (honeypot && honeypot.value.trim() !== "") {
      // ボットが入力した場合は静かに無視（成功風の挙動でログのみ残す）
      console.warn("[contact-form] honeypot triggered");
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setStatus("送信中…", "loading");
    if (submitBtn) submitBtn.disabled = true;

    const formData = new FormData(form);

    // 自動検出順:
    //   1. opts.endpoint（明示指定があれば最優先）
    //   2. Web3Forms（フォーム内に access_key hidden field があれば api.web3forms.com）
    //   3. Netlify Forms（data-netlify="true" 属性があれば現在のページにPOST）
    const accessKey = form.querySelector<HTMLInputElement>("input[name='access_key']")?.value;
    const isWeb3Forms = Boolean(accessKey && accessKey.trim() !== "");
    const isNetlifyForm = form.getAttribute("data-netlify") === "true";
    const target =
      opts.endpoint ??
      (isWeb3Forms
        ? "https://api.web3forms.com/submit"
        : isNetlifyForm
          ? "/"
          : undefined);

    try {
      if (target) {
        await postToEndpoint(target, formData, fetcher);
        setStatus(
          "お問い合わせいただきありがとうございました。担当者より追ってご連絡いたします。",
          "success",
        );
        form.reset();
        form.querySelectorAll<HTMLElement>("[data-case]").forEach((el) => {
          el.hidden = true;
        });
      } else if (opts.mailto) {
        const url = buildMailto(opts.mailto, formData);
        // ユーザーのメーラーを開く
        window.location.href = url;
        setStatus(
          "メールアプリを起動しました。本文をご確認のうえ送信を完了してください。",
          "success",
        );
      } else {
        setStatus(
          "送信先が設定されていません。サイト管理者にお問い合わせください。",
          "error",
        );
      }
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "送信に失敗しました。お時間をおいて再度お試しください。";
      setStatus(msg, "error");
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });
}

async function postToEndpoint(
  endpoint: string,
  formData: FormData,
  fetcher: typeof fetch,
): Promise<void> {
  const res = await fetcher(endpoint, {
    method: "POST",
    headers: { Accept: "application/json" },
    body: formData,
  });
  if (res.ok) return;
  // 失敗時はサーバ由来のエラーメッセージを取り出して投げる
  let detail = "送信に失敗しました。お時間をおいて再度お試しください。";
  try {
    const data = await res.json();
    if (data?.errors?.[0]?.message) detail = String(data.errors[0].message);
    else if (typeof data?.error === "string") detail = data.error;
  } catch {
    /* ignore JSON parse errors */
  }
  throw new Error(detail);
}

export function buildMailto(to: string, formData: FormData): string {
  const subject = "【Web】バレルサウナのお問い合わせ";
  const body = formatBody(formData);
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function formatBody(data: FormData): string {
  const lines: string[] = [];
  lines.push("グランピング沖縄 バレルサウナ お問い合わせ");
  lines.push("─────────────────────────");
  for (const key of Object.keys(FIELD_LABELS)) {
    const raw = data.get(key);
    if (raw == null || raw === "") continue;
    const label = FIELD_LABELS[key];
    let value = String(raw);
    if (key === "useCase") value = USE_CASE_LABELS[value] ?? value;
    if (key === "facilityType") value = FACILITY_LABELS[value] ?? value;
    if (key === "placement") value = PLACEMENT_LABELS[value] ?? value;
    lines.push(`■ ${label}`);
    lines.push(value);
    lines.push("");
  }
  return lines.join("\n");
}
